import {
  CodeBlock,
  Heading,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  VStack,
} from '@astryxdesign/core';

/**
 * "q50 is not the mean."
 *
 * Every number below is copied from docs/marketing/assets/forecast-demo.json —
 * a real /v1/forecast response for the canonical marketing series (weekly
 * units for one SKU, 96 points, horizon 12), fetched from the live API by
 * scripts/brand/fetch-forecast.mjs. Nothing is invented to make the skew look
 * larger than it is; on this series it is small, and the article says so.
 */

/** The last twelve observations of the request, so the chart has a run-in. */
const HISTORY_TAIL = [543, 518, 478, 485, 444, 454, 495, 502, 564, 563, 557, 598];

const Q = {
  q10: [533.0, 512.7, 491.6, 459.8, 455.9, 453.1, 452.1, 460.3, 470.8, 497.2, 506.4, 507.2],
  q20: [544.3, 526.5, 506.7, 477.5, 474.1, 472.3, 473.6, 483.5, 492.8, 523.0, 530.6, 539.0],
  q30: [553.4, 535.3, 517.8, 489.3, 485.5, 486.6, 487.4, 500.8, 509.7, 539.3, 550.8, 554.0],
  q40: [559.7, 544.5, 527.5, 497.5, 495.9, 496.7, 500.4, 514.4, 525.1, 556.4, 566.7, 573.3],
  q50: [564.2, 550.2, 534.0, 507.3, 506.4, 507.2, 511.5, 525.4, 538.7, 570.6, 582.9, 588.8],
  q60: [570.1, 556.6, 542.2, 515.8, 516.8, 517.5, 522.0, 538.5, 551.4, 583.0, 598.0, 607.0],
  q70: [576.2, 566.6, 551.6, 526.0, 528.5, 528.2, 535.8, 550.0, 565.9, 599.7, 614.3, 625.3],
  q80: [583.5, 573.5, 561.2, 536.3, 540.2, 542.7, 548.6, 565.3, 582.0, 617.4, 634.5, 642.5],
  q90: [595.0, 585.4, 575.5, 555.6, 558.9, 562.5, 571.6, 590.6, 605.9, 646.3, 660.8, 673.0],
};

const MEAN = [565.7, 554.4, 527.4, 506.5, 506.1, 508.4, 509.8, 519.1, 534.7, 569.6, 579.4, 581.9];

const RESPONSE_EXCERPT = `{
  "forecasts": [{
    "index": 0,
    "point": [564.2, 550.2, 534.0, …, 588.8],   // == quantiles.q50
    "mean":  [565.7, 554.4, 527.4, …, 581.9],   // the distribution mean
    "quantiles": {
      "q10": [533.0, 512.7, 491.6, …, 507.2],
      "q20": [544.3, …], "q30": […], "q40": […],
      "q50": [564.2, 550.2, 534.0, …, 588.8],
      "q60": […], "q70": […], "q80": […],
      "q90": [595.0, 585.4, 575.5, …, 673.0]
    }
  }]
}`;

const NEWSVENDOR = `# Pick the quantile from the cost of being wrong in each direction.
#   cu = cost of one unit short (lost margin, expedite fee)
#   co = cost of one unit over (holding, markdown, waste)
cu, co = 12.0, 3.0
fractile = cu / (cu + co)          # 0.80 → plan on q80

q = out["forecasts"][0]["quantiles"]
key = f"q{int(round(fractile * 10) * 10)}"   # nearest decile the API returns
order_up_to = q[key]               # one number per horizon step`;

const COVERAGE_CHECK = `# Hold out the last H points, forecast them, count how many land in the band.
hist, truth = series[:-H], series[-H:]
q = forecast(hist, horizon=H)["forecasts"][0]["quantiles"]
inside = sum(lo <= y <= hi for lo, y, hi in zip(q["q10"], truth, q["q90"]))
print(f"{inside}/{H} inside the 80% band")   # aim for ~0.8·H over many series`;

/**
 * A fan chart of the nine deciles, hand-rolled for the same reasons as
 * ForecastChart: a dozen lines of geometry do not justify a dependency, and
 * the tokens keep it legible in both themes.
 */
function FanChart() {
  const width = 720;
  const height = 280;
  const pad = { top: 16, right: 16, bottom: 30, left: 46 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const all = [...HISTORY_TAIL, ...Q.q10, ...Q.q90];
  const min = Math.min(...all) - 20;
  const max = Math.max(...all) + 20;
  const total = HISTORY_TAIL.length + Q.q50.length - 1;
  const x = (i: number) => pad.left + (i / total) * plotW;
  const y = (v: number) => pad.top + (1 - (v - min) / (max - min)) * plotH;
  const off = HISTORY_TAIL.length - 1;
  const join = HISTORY_TAIL[HISTORY_TAIL.length - 1]!;

  const band = (lo: number[], hi: number[]) => {
    const up = [`${x(off)},${y(join)}`, ...hi.map((v, i) => `${x(off + 1 + i)},${y(v)}`)];
    const dn = [...lo.map((v, i) => `${x(off + 1 + i)},${y(v)}`)].reverse();
    return `M ${up.join(' L ')} L ${dn.join(' L ')} Z`;
  };
  const line = (vals: number[]) =>
    [`${x(off)},${y(join)}`, ...vals.map((v, i) => `${x(off + 1 + i)},${y(v)}`)].join(' ');

  const bands: Array<[number[], number[], number]> = [
    [Q.q10, Q.q90, 0.12],
    [Q.q20, Q.q80, 0.14],
    [Q.q30, Q.q70, 0.16],
    [Q.q40, Q.q60, 0.2],
  ];

  return (
    <VStack gap={1.5}>
      <div className="chart-wrap">
        <svg
          className="chart"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label="Fan chart of a twelve-step forecast: nested decile bands from q10 to q90 around the median, with the distribution mean drawn as a dashed line slightly below the median in the later steps"
        >
          {[min + 20, (min + max) / 2, max - 20].map((t) => (
            <g key={t}>
              <line x1={pad.left} x2={width - pad.right} y1={y(t)} y2={y(t)} stroke="var(--color-border)" />
              <text x={pad.left - 8} y={y(t) + 4} textAnchor="end" fontSize={11} fill="var(--color-text-secondary)">
                {Math.round(t)}
              </text>
            </g>
          ))}
          {bands.map(([lo, hi, o], i) => (
            <path key={i} d={band(lo, hi)} fill="var(--vt-accent)" opacity={o} />
          ))}
          <polyline
            points={HISTORY_TAIL.map((v, i) => `${x(i)},${y(v)}`).join(' ')}
            fill="none"
            stroke="var(--color-text-primary)"
            strokeWidth={1.6}
          />
          <polyline points={line(Q.q50)} fill="none" stroke="var(--vt-accent)" strokeWidth={2.2} />
          <polyline points={line(MEAN)} fill="none" stroke="var(--vt-accent-3)" strokeWidth={1.6} strokeDasharray="5 4" />
          <line x1={x(off)} x2={x(off)} y1={pad.top} y2={height - pad.bottom} stroke="var(--color-border-emphasized)" strokeDasharray="4 4" />
          <text x={x(off) + 6} y={pad.top + 12} fontSize={11} fill="var(--color-text-secondary)">
            forecast →
          </text>
          <text x={pad.left} y={height - 8} fontSize={11} fill="var(--color-text-secondary)">
            last 12 observed
          </text>
          <text x={width - pad.right} y={height - 8} textAnchor="end" fontSize={11} fill="var(--color-text-secondary)">
            +12 steps
          </text>
        </svg>
      </div>
      <div className="legend">
        <span className="legend__item">
          <span className="legend__swatch" style={{ background: 'var(--color-text-primary)' }} />
          Observed
        </span>
        <span className="legend__item">
          <span className="legend__swatch" style={{ background: 'var(--vt-accent)' }} />
          q50 (median)
        </span>
        <span className="legend__item">
          <span className="legend__swatch" style={{ background: 'var(--vt-accent-3)' }} />
          mean
        </span>
        <span className="legend__item">
          <span className="legend__swatch" style={{ background: 'var(--vt-accent)', opacity: 0.35, height: 9 }} />
          q10–q90, q20–q80, q30–q70, q40–q60
        </span>
      </div>
    </VStack>
  );
}

export function QuantileForecastingExplainedArticle() {
  return (
    <>
      <Text>
        A probabilistic forecast does not give you one number per future step. It gives you a
        distribution, and the API hands you eleven summaries of it: nine quantiles from{' '}
        <span className="mono">q10</span> to <span className="mono">q90</span>, a{' '}
        <span className="mono">point</span> that is exactly <span className="mono">q50</span>, and a{' '}
        <span className="mono">mean</span>. The two mistakes people make are reading{' '}
        <span className="mono">q50</span> as the average and reading <span className="mono">q90</span>{' '}
        as the worst case. Neither is true, and on a real series the gap is visible.
      </Text>

      <div className="callout">
        <Text weight="medium">The short version</Text>
        <Text color="secondary">
          <span className="mono">q50</span> is the value the future is equally likely to land above or
          below. <span className="mono">mean</span> is the expected value, which sits off the median
          whenever the distribution is lopsided. <span className="mono">q90</span> is the value the
          future lands under nine times in ten, not the ceiling. Plan inventory on the quantile that
          matches your cost of being wrong, and check the band on held-out data before you trust it.
        </Text>
      </div>

      <VStack gap={2}>
        <Heading level={2} id="what-a-quantile-promises">What a quantile promises</Heading>
        <Text color="secondary">
          The q<em>k</em> quantile at step <em>h</em> is the value that the outcome at step <em>h</em>{' '}
          should fall below with probability <em>k</em>%. That is the whole definition. <span className="mono">q10</span>{' '}
          is not a lower bound and <span className="mono">q90</span> is not an upper bound: one time
          in ten the real number is below the first, and one time in ten it is above the second. The
          pair together form an 80% interval, which is why the benchmark article measures{' '}
          <Link href="/blog/timesfm-vs-prophet-benchmark#intervals">coverage against 80%</Link>{' '}
          rather than against 100%.
        </Text>
        <Text color="secondary">
          Because each quantile is defined per step, the band widens with the horizon on almost every
          series. That is not the model getting worse; it is the model being honest that twelve weeks
          out is less knowable than one.
        </Text>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="a-real-response">A real response, read line by line</Heading>
        <Text color="secondary">
          This is the response for the series that appears in every screenshot on this site — 96
          weekly observations for one SKU, horizon 12, from a free-tier key. Only the arrays are
          abbreviated.
        </Text>
        <div className="code-frame">
          <CodeBlock code={RESPONSE_EXCERPT} language="json" hasCopyButton container="card" />
        </div>
        <FanChart />
        <Text size="sm" color="secondary">
          The nested bands are the decile pairs: q10–q90 outermost, q40–q60 innermost. The dashed line
          is the mean. Everything plotted is copied from the response above.
        </Text>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="mean-vs-median">Mean versus median</Heading>
        <Text color="secondary">
          On a symmetric distribution the two coincide, and on this series they nearly do for the first
          few steps. From step 8 on the mean sits below the median by 5 to 7 units — the model puts a
          longer tail on the downside than the upside. That is a small skew, and this article is not
          going to pretend it is dramatic. It is simply the reason the API returns both: if you sum{' '}
          <span className="mono">q50</span> across 500 SKUs to get a total, you get the sum of medians,
          which is not the median of the total and is not its expectation either. Sum the{' '}
          <span className="mono">mean</span> for that.
        </Text>

        <Table density="compact" dividers="rows">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Step</TableHeaderCell>
              <TableHeaderCell>q10</TableHeaderCell>
              <TableHeaderCell>q50</TableHeaderCell>
              <TableHeaderCell>mean</TableHeaderCell>
              <TableHeaderCell>q90</TableHeaderCell>
              <TableHeaderCell>mean − q50</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[0, 3, 7, 9, 11].map((i) => (
              <TableRow key={i}>
                <TableCell>
                  <Text size="sm">{i + 1}</Text>
                </TableCell>
                <TableCell>
                  <span className="mono">{Q.q10[i]!.toFixed(1)}</span>
                </TableCell>
                <TableCell>
                  <span className="mono">{Q.q50[i]!.toFixed(1)}</span>
                </TableCell>
                <TableCell>
                  <span className="mono">{MEAN[i]!.toFixed(1)}</span>
                </TableCell>
                <TableCell>
                  <span className="mono">{Q.q90[i]!.toFixed(1)}</span>
                </TableCell>
                <TableCell>
                  <span className="mono">{(MEAN[i]! - Q.q50[i]!).toFixed(1)}</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Text size="sm" color="secondary">
          Rows are a sample of the twelve steps. Widths: the q10–q90 band is 62 units wide at step 1
          and 166 wide at step 12.
        </Text>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="which-line-to-plan-on">Which line to plan on</Heading>
        <Text color="secondary">
          The median is the right number to show on a chart. It is almost never the right number to
          order stock against, because the cost of being short and the cost of being over are not
          equal. The classic result — the newsvendor fractile — says the optimal quantile is{' '}
          <span className="mono">cu / (cu + co)</span>, where <span className="mono">cu</span> is the
          cost of one unit short and <span className="mono">co</span> the cost of one unit over. Lost
          margin of 12 against holding cost of 3 puts you at the 80th percentile, and the API already
          returns it.
        </Text>
        <div className="code-frame">
          <CodeBlock code={NEWSVENDOR} language="python" hasCopyButton container="card" />
        </div>
        <Text color="secondary">
          The same logic runs the other way for alerts. An anomaly detector that pages when a metric
          crosses <span className="mono">q10</span> or <span className="mono">q90</span> will, by
          construction, page on roughly one in five clean points. Use q10 and q90 for a warning
          colour on a dashboard; for a page, widen the band or require two consecutive points
          outside it.
        </Text>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="checking-calibration">Checking the band is honest</Heading>
        <Text color="secondary">
          A quantile is a promise, and promises can be checked. Hold out the last <em>H</em> points of
          a series, forecast them from the rest, and count how many land between q10 and q90. Over
          enough series the count should be close to 80%. On the 300 M4 series in our benchmark
          TimesFM landed at 82.5%, 86.8% and 75.8% by frequency; Prophet&apos;s 80% interval landed at
          65.0%, 58.5% and 44.8%. A band that covers half of reality while claiming 80% is worse than
          no band, because it will be planned on.
        </Text>
        <div className="code-frame">
          <CodeBlock code={COVERAGE_CHECK} language="python" hasCopyButton container="card" />
        </div>
        <Text size="sm" color="secondary">
          On the single series in this article the last 12 points all fell inside the band — 12 of
          12, against a promise of roughly 10. One series is an anecdote, not a calibration; run it on
          dozens before drawing a conclusion.
        </Text>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="when-quantiles-are-not-enough">When this is the wrong tool</Heading>
        <VStack gap={1.5}>
          <Text color="secondary">
            <strong>You need the joint distribution.</strong> The quantiles are marginal, per step.
            They do not tell you the probability that <em>all twelve</em> weeks come in under q90, and
            multiplying 0.9 by itself twelve times is wrong because the steps are correlated. If you
            need a distribution over the twelve-week total, sample paths, which this API does not
            return.
          </Text>
          <Text color="secondary">
            <strong>You need finer tails than the deciles.</strong> The response stops at q10 and q90.
            A 99th-percentile capacity plan needs a model that returns it or a fitted tail; do not
            extrapolate the deciles.
          </Text>
          <Text color="secondary">
            <strong>Your series has fewer than about thirty points.</strong> The band will be wide and
            the median flat, which is the model saying it does not know. That is honest, but it is
            not useful for planning; use a category-level series until the history is there.
          </Text>
        </VStack>
      </VStack>
    </>
  );
}
